import jsPDF from 'jspdf';
import 'jspdf-autotable';
import moment from 'moment';
import _utils from './Utils';

// Recruitment Candidate-List Publication Modernization phase.
//
// Reuses the exact jsPDF + jspdf-autotable pattern already proven in AttendanceDataTable.jsx
// (multi-row, auto-paginating, vector-text tables — not the html2canvas single-candidate rasterization
// used by Receipt/Hall Ticket) and completes the letterhead-repeats-per-page hook that file left
// commented-out. Columns match the actual production reference documents
// (Junior_Clerk_1st_Patrata_Yadi.pdf / Junior_Clerk_Final_Patrata_Yadi.pdf): Application ID, First/
// Middle/Last Name, Gender, Applied Post — modernized with letterhead, page numbers and professional
// table styling, without changing that business content, ordering (Application ID ascending, already
// guaranteed by StudentRepository.GetEligibleCandidates), or post-wise scope.
//
// `stage` is 'First' or 'Final' — purely a label choice made by the caller (EligibleCandidateList_new.jsx
// derives it from whether a Published Eligible Candidates Publication already exists for this post); it
// has no effect on the underlying eligibility query, which is identical either way.
function buildEligibleCandidateListPdf({ entityName, examName, postName, stage, candidates, generatedAt }) {
    const list = candidates || [];
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4', compress: true });
    const pageWidth = doc.internal.pageSize.getWidth();
    const marginX = 40;
    const headerHeight = 118;
    const generatedAtText = moment(generatedAt || new Date()).format('DD-MMM-YYYY hh:mm A');
    const title = stage === 'Final' ? 'FINAL ELIGIBLE CANDIDATES LIST' : 'FIRST ELIGIBLE CANDIDATES LIST';

    // PDF Filename Closure phase: the browser's built-in PDF viewer (Chrome/Edge) suggests this
    // document's own /Title metadata as the filename when a user saves/downloads it from an opened
    // blob: URL — which is exactly the "Preview" step below, before the file is ever published/uploaded
    // anywhere. Without this, that save falls back to the blob: URL's own internal UUID, which is what
    // was previously observed. Computed from the same buildEligibleCandidateListFileName the caller
    // uses for the actual Publish upload, so the two never drift apart — one filename, one source.
    const suggestedFileName = _utils.buildEligibleCandidateListFileName(stage, postName, generatedAt);
    doc.setProperties({ title: suggestedFileName.replace(/\.pdf$/i, '') });

    const body = list.map((c, index) => [
        index + 1,
        c.Application_Id != null ? c.Application_Id : '',
        c.First_Name || '',
        c.Middle_Name || '',
        c.Last_Name || '',
        c.Gender || '',
        c.Applied_Post || postName || ''
    ]);

    const drawLetterhead = () => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(15);
        doc.setTextColor(21, 61, 108);
        doc.text(entityName || '', pageWidth / 2, 38, { align: 'center' });

        if (examName) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(11);
            doc.setTextColor(70, 70, 70);
            doc.text(examName, pageWidth / 2, 56, { align: 'center' });
        }

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.setTextColor(0, 0, 0);
        doc.text(title, pageWidth / 2, 78, { align: 'center' });

        doc.setDrawColor(21, 61, 108);
        doc.setLineWidth(1);
        doc.line(marginX, 88, pageWidth - marginX, 88);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        doc.text(postName ? ('Post: ' + postName) : 'All Posts', marginX, 102);
        doc.text('Generated: ' + generatedAtText, pageWidth - marginX, 102, { align: 'right' });
    };

    doc.autoTable({
        head: [['Sr.No', 'Application ID', 'First Name', 'Middle Name', 'Last Name', 'Gender', 'Applied Post']],
        body,
        startY: headerHeight,
        margin: { top: headerHeight, left: marginX, right: marginX, bottom: 50 },
        styles: { font: 'helvetica', fontSize: 9, cellPadding: 5, overflow: 'linebreak', lineColor: [220, 220, 220], lineWidth: 0.5 },
        headStyles: { fillColor: [21, 61, 108], textColor: 255, fontStyle: 'bold', halign: 'center' },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        columnStyles: {
            0: { cellWidth: 34, halign: 'center' },
            1: { cellWidth: 72, halign: 'center' },
            5: { cellWidth: 56, halign: 'center' },
            6: { cellWidth: 110 }
        },
        didDrawPage: drawLetterhead
    });

    // "Page X of Y" needs the final page count, which jsPDF/autoTable only knows once every page has
    // already been drawn — drawn here in a second pass over every page rather than inside didDrawPage
    // (which only ever sees the page count SO FAR, not the eventual total).
    const totalPages = doc.internal.getNumberOfPages();
    const pageHeight = doc.internal.pageSize.getHeight();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(120, 120, 120);
        doc.text('Total Eligible Candidates: ' + list.length, marginX, pageHeight - 20);
        doc.text('Page ' + i + ' of ' + totalPages, pageWidth - marginX, pageHeight - 20, { align: 'right' });
    }

    return doc;
}

function buildEligibleCandidateListPdfBlob(params) {
    return buildEligibleCandidateListPdf(params).output('blob');
}

export { buildEligibleCandidateListPdf, buildEligibleCandidateListPdfBlob };
export default buildEligibleCandidateListPdfBlob;
